// useDokumentDateien.ts — Dateien, die an einem Dokumenteintrag der
// Kontaktanlage hängen (Ausweiskopie, Führerschein, Geburts-/Sterbeurkunde …)
// und beim Speichern in der Dokumentablage des Kontakts landen.
//
// Muster wie die Geburtsurkunde in `NewPrivateContactForm`: Beim Anlegen gibt
// es noch keinen Kontakt, an den die Datei gehängt werden könnte — sie wird
// deshalb vorgemerkt und erst nach dem Speichern abgelegt, mit der
// Dokumentart als Kategorie. Eigene Datei, damit die (viel bearbeitete) Maske
// nur ein paar Zeilen Verdrahtung braucht.

import * as React from "react";
import { documentService } from "../../services/documentService";
import type { AusweisdokumentEintrag } from "./widgets/AusweisdokumentAbschnitt";

export type DokumentDateien = {
  name: (docId: string) => string | undefined;
  setzen: (docId: string, datei: File) => void;
  entfernen: (docId: string) => void;
  /**
   * Legt alle vorgemerkten Dateien am Kontakt ab — nur die von Einträgen, die
   * es noch gibt. Liefert die Fehlermeldungen; abgelegte Dateien werden
   * vergessen, damit ein zweites Speichern nichts doppelt ablegt.
   */
  ablegen: (contactId: number, docs: AusweisdokumentEintrag[]) => Promise<string[]>;
};

export function useDokumentDateien(): DokumentDateien {
  const [dateien, setDateien] = React.useState<Record<string, File>>({});
  // Die Ablage läuft nach einem await im Speichern — sie braucht den
  // aktuellen Stand, nicht den des Renderns, in dem der Klick passierte.
  const aktuell = React.useRef(dateien);
  aktuell.current = dateien;

  const name = React.useCallback((docId: string) => dateien[docId]?.name, [dateien]);
  const setzen = React.useCallback((docId: string, datei: File) => {
    setDateien((vorher) => ({ ...vorher, [docId]: datei }));
  }, []);
  const entfernen = React.useCallback((docId: string) => {
    setDateien((vorher) => {
      const naechste = { ...vorher };
      delete naechste[docId];
      return naechste;
    });
  }, []);

  const ablegen = React.useCallback(async (contactId: number, docs: AusweisdokumentEintrag[]) => {
    const probleme: string[] = [];
    const erledigt: string[] = [];
    for (const [docId, datei] of Object.entries(aktuell.current)) {
      const doc = docs.find((d) => d.id === docId);
      if (!doc) continue;
      const art = (doc.idCardType || "").trim() || "Ausweisdokument";
      try {
        await documentService.upload({ file: datei, contactId, category: art });
        erledigt.push(docId);
      } catch {
        probleme.push(`${art} „${datei.name}" konnte nicht abgelegt werden.`);
      }
    }
    if (erledigt.length) {
      const rest = { ...aktuell.current };
      for (const id of erledigt) delete rest[id];
      aktuell.current = rest;
      setDateien((vorher) => {
        const naechste = { ...vorher };
        for (const id of erledigt) delete naechste[id];
        return naechste;
      });
    }
    return probleme;
  }, []);

  return { name, setzen, entfernen, ablegen };
}
